import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Clock, Phone, MessageCircle, CheckCircle2, FileText, AlertTriangle, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'arthik_tax_popup_dismissed_session';
const PHONES = ['+918104859801', '+917054986771'];
const WHATSAPP_NUMBER = '917054986771';

// Compute the next occurrence of (month, day) from today (UTC + IST offset handled loosely — we just use IST date)
function nextOccurrence(month, day) {
  const now = new Date();
  let year = now.getFullYear();
  let candidate = new Date(year, month - 1, day, 23, 59, 59);
  if (candidate < now) {
    candidate = new Date(year + 1, month - 1, day, 23, 59, 59);
  }
  return candidate;
}

function daysBetween(target) {
  const ms = target - new Date();
  if (ms <= 0) return { days: 0, hours: 0, expired: true };
  return {
    days: Math.floor(ms / (1000 * 60 * 60 * 24)),
    hours: Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    expired: false,
  };
}

// Build deadline list freshly each render so it stays accurate
function getDeadlines() {
  const now = new Date();
  // GSTR-1: 11th of every month; GSTR-3B: 20th of every month
  const gstr1Day = 11;
  const gstr3bDay = 20;
  let gstr1 = new Date(now.getFullYear(), now.getMonth(), gstr1Day, 23, 59, 59);
  if (gstr1 < now) gstr1 = new Date(now.getFullYear(), now.getMonth() + 1, gstr1Day, 23, 59, 59);
  let gstr3b = new Date(now.getFullYear(), now.getMonth(), gstr3bDay, 23, 59, 59);
  if (gstr3b < now) gstr3b = new Date(now.getFullYear(), now.getMonth() + 1, gstr3bDay, 23, 59, 59);

  return [
    {
      key: 'itr',
      label: 'ITR Filing (Individual / Salaried)',
      target: nextOccurrence(7, 31), // 31 July
      penaltyHint: 'Late fee up to ₹5,000 u/s 234F',
      color: '#DC2626',
      icon: '📄',
    },
    {
      key: 'tax_audit',
      label: 'Tax Audit Report (44AB)',
      target: nextOccurrence(9, 30), // 30 Sept
      penaltyHint: 'Penalty 0.5% turnover (max ₹1.5L)',
      color: '#EA580C',
      icon: '🔍',
    },
    {
      key: 'itr_audit',
      label: 'ITR (Audit cases / Corporates)',
      target: nextOccurrence(10, 31), // 31 Oct
      penaltyHint: 'Interest u/s 234A, 234B continues',
      color: '#D97706',
      icon: '🏢',
    },
    {
      key: 'gstr1',
      label: 'GSTR-1 (Monthly Outward Supplies)',
      target: gstr1,
      penaltyHint: '₹50/day late fee per Act',
      color: '#0891B2',
      icon: '🧾',
    },
    {
      key: 'gstr3b',
      label: 'GSTR-3B (Monthly Summary)',
      target: gstr3b,
      penaltyHint: 'Interest 18% p.a. on tax dues',
      color: '#0E7490',
      icon: '💰',
    },
    {
      key: 'tds',
      label: 'TDS Return (Quarterly Q4)',
      target: nextOccurrence(5, 31), // 31 May
      penaltyHint: '₹200/day late fee u/s 234E',
      color: '#7C3AED',
      icon: '⚡',
    },
  ].sort((a, b) => a.target - b.target);
}

const SERVICES = [
  { key: 'gst', label: 'GST Filing & Registration', icon: '📊', tagline: 'GSTR-1, 3B, 9 · ITC reconciliation', from: 499, route: '/tax-services/gst-filing' },
  { key: 'itr', label: 'Income Tax Return (ITR)', icon: '📄', tagline: 'ITR-1 to ITR-7 · Old vs New regime', from: 499, route: '/tax-services/itr-filing' },
  { key: 'tds', label: 'TDS / TCS Return', icon: '⚡', tagline: '24Q, 26Q, 27Q · Form 16/16A', from: 999, route: '/tax-services/tds-filing' },
  { key: 'company', label: 'Company Registration', icon: '🏢', tagline: 'Pvt Ltd · OPC · LLP · MoA & AoA', from: 999, route: '/tax-services/business-registration' },
  { key: 'audit', label: 'Accounting & Audit', icon: '🔍', tagline: 'Books · 44AB · Statutory Audit', from: 2999, route: '/tax-services' },
  { key: 'msme', label: 'MSME / Udyam Reg.', icon: '📝', tagline: 'Udyam · IEC · FSSAI · Trade Licence', from: 599, route: '/tax-services' },
  { key: 'prof', label: 'Professional Tax', icon: '👔', tagline: 'PT registration & filing', from: 499, route: '/tax-services' },
  { key: 'mca', label: 'ROC / MCA Filings', icon: '📁', tagline: 'AOC-4 · MGT-7 · DIR-3 KYC', from: 1499, route: '/tax-services' },
];

const TaxUrgencyPopup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [, forceTick] = useState(0);
  const deadlines = useMemo(() => getDeadlines(), []);

  useEffect(() => {
    // open once per browser session, 4 sec after landing
    if (sessionStorage.getItem(STORAGE_KEY) === '1') {
      setShowFab(true);
      return;
    }
    const t = setTimeout(() => setOpen(true), 4500);
    return () => clearTimeout(t);
  }, []);

  // Re-render every minute to keep countdowns accurate
  useEffect(() => {
    const t = setInterval(() => forceTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setShowFab(true);
    sessionStorage.setItem(STORAGE_KEY, '1');
  };

  const handleApply = (route) => {
    handleClose();
    navigate(route);
  };

  const handleWhatsApp = (deadline) => {
    const msg = deadline
      ? `Hi Arthik, I want to file ${deadline.label} (deadline ${deadline.target.toLocaleDateString('en-IN')}). Please help.`
      : `Hi Arthik, I need help with tax filing. Please share details.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleCall = () => {
    window.open(`tel:${PHONES[1]}`);
  };

  const nearest = deadlines[0];
  const nearestCountdown = nearest ? daysBetween(nearest.target) : null;

  return (
    <>
      {/* Floating re-open pill */}
      {showFab && !open && (
        <button
          onClick={() => { setOpen(true); setShowFab(false); }}
          data-testid="tax-popup-fab"
          aria-label="View tax deadlines"
          style={{
            position: 'fixed',
            bottom: '6rem',
            left: '1.25rem',
            zIndex: 9998,
            background: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)',
            color: 'white',
            border: 0,
            borderRadius: 999,
            padding: '0.65rem 1rem',
            fontSize: '0.8rem',
            fontWeight: 800,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 10px 28px rgba(220,38,38,0.45)',
            animation: 'tax-pulse 2s ease-in-out infinite',
          }}
        >
          <Clock size={14} /> ⏰ ITR Deadline
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="tax-urgency-popup"
          onClick={(e) => { if (e.target.classList.contains('tax-pop-overlay')) handleClose(); }}
          className="tax-pop-overlay"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.78)', backdropFilter: 'blur(8px)',
            zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
            animation: 'tax-fade .25s ease',
          }}
        >
          <div style={{
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF7ED 100%)',
            borderRadius: 20, maxWidth: 920, width: '100%', maxHeight: '92vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', boxShadow: '0 40px 100px rgba(0,0,0,0.55)',
            position: 'relative',
            animation: 'tax-slide .35s cubic-bezier(.2,.8,.2,1)',
          }}>
            <button
              onClick={handleClose}
              aria-label="Close"
              data-testid="tax-popup-close"
              style={{
                position: 'absolute', top: 14, right: 14, background: 'rgba(255,255,255,0.85)',
                border: 0, width: 34, height: 34, borderRadius: 50, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#0F172A', zIndex: 5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <X size={18} />
            </button>

            {/* HEADER — Urgency */}
            <div style={{
              background: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)',
              color: 'white', padding: '1.75rem 1.75rem 1.5rem', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(255,255,255,0.15), transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.18)', padding: '4px 12px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.1em' }}>
                <AlertTriangle size={12} /> URGENT · TAX DEADLINES APPROACHING
              </div>
              <h2 style={{ margin: '12px 0 6px', fontSize: 'clamp(1.4rem, 3vw, 1.9rem)', fontWeight: 800, fontFamily: 'Outfit, sans-serif' }}>
                Don't pay penalties. <span style={{ color: '#FEF3C7' }}>File on time with Arthik.</span>
              </h2>
              <p style={{ margin: 0, opacity: 0.92, fontSize: '0.92rem' }}>
                CA-verified filing · 100% accurate · Doorstep service across India · Free consultation for first-time clients
              </p>

              {nearest && !nearestCountdown.expired && (
                <div style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', gap: 14, background: 'rgba(15,23,42,0.35)', padding: '10px 16px', borderRadius: 12 }}>
                  <span style={{ fontSize: '1.4rem' }}>{nearest.icon}</span>
                  <div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Next Deadline · {nearest.label}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, marginTop: 2 }}>
                      ⏳ {nearestCountdown.days}d {nearestCountdown.hours}h left
                      <span style={{ marginLeft: 10, fontSize: '0.78rem', opacity: 0.85, fontWeight: 500 }}>
                        ({nearest.target.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })})
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* BODY */}
            <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1 }}>
              {/* Deadlines */}
              <h3 style={{ margin: '0 0 0.85rem', fontFamily: 'Outfit, sans-serif', color: '#0F172A', fontSize: '1.05rem', fontWeight: 800 }}>
                📅 Upcoming statutory deadlines
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginBottom: '1.5rem' }} data-testid="tax-deadlines-grid">
                {deadlines.slice(0, 4).map((d) => {
                  const c = daysBetween(d.target);
                  return (
                    <div key={d.key} style={{
                      background: 'white', borderRadius: 12, padding: '12px 14px',
                      border: `1px solid ${d.color}30`, borderLeft: `3px solid ${d.color}`,
                    }} data-testid={`deadline-${d.key}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '1.3rem' }}>{d.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.8rem', color: '#0F172A', fontWeight: 700, lineHeight: 1.25 }}>{d.label}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: 2 }}>
                            Due <strong style={{ color: d.color }}>{d.target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</strong> · {c.expired ? 'today!' : `${c.days}d ${c.hours}h`}
                          </div>
                        </div>
                      </div>
                      <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#991B1B', background: '#FEF2F2', padding: '4px 8px', borderRadius: 6, display: 'inline-block' }}>
                        ⚠ {d.penaltyHint}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Services grid */}
              <h3 style={{ margin: '0 0 0.85rem', fontFamily: 'Outfit, sans-serif', color: '#0F172A', fontSize: '1.05rem', fontWeight: 800 }}>
                💼 Pick a service · CA support included
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginBottom: '1.5rem' }} data-testid="tax-services-grid">
                {SERVICES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => handleApply(s.route)}
                    data-testid={`tax-svc-${s.key}`}
                    style={{
                      background: 'white', borderRadius: 12, padding: '14px',
                      border: '1px solid #E2E8F0', textAlign: 'left', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', gap: 6,
                      transition: 'all .15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B35'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,53,0.18)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '1.3rem' }}>{s.icon}</span>
                      <strong style={{ fontSize: '0.85rem', color: '#0F172A' }}>{s.label}</strong>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748B' }}>{s.tagline}</div>
                    <div style={{ fontSize: '0.78rem', color: '#FF6B35', fontWeight: 800, marginTop: 2 }}>From ₹{s.from} →</div>
                  </button>
                ))}
              </div>

              {/* Why choose us */}
              <div style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', borderRadius: 12, padding: '12px 14px', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', fontSize: '0.78rem', color: '#065F46', fontWeight: 600 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Expert CAs</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Fast processing</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> 100% accuracy</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Best rates</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Online + doorstep</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Data secure</span>
                </div>
              </div>
            </div>

            {/* FOOTER CTAs */}
            <div style={{
              background: '#0F172A', padding: '1.1rem 1.5rem',
              display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#FEF3C7', fontSize: '0.85rem', fontWeight: 700 }}>
                <Sparkles size={14} /> FREE CONSULTATION — Call & Save!
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={handleCall} data-testid="tax-popup-call" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.7rem 1.1rem',
                  background: 'white', color: '#0F172A', fontWeight: 800, fontSize: '0.85rem',
                  border: 0, borderRadius: 10, cursor: 'pointer',
                }}>
                  <Phone size={15} /> Call Now
                </button>
                <button onClick={() => handleWhatsApp(nearest)} data-testid="tax-popup-whatsapp" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.7rem 1.1rem',
                  background: '#25D366', color: 'white', fontWeight: 800, fontSize: '0.85rem',
                  border: 0, borderRadius: 10, cursor: 'pointer',
                }}>
                  <MessageCircle size={15} /> WhatsApp
                </button>
                <button onClick={() => handleApply('/tax-services')} data-testid="tax-popup-apply" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.7rem 1.4rem',
                  background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)', color: 'white', fontWeight: 800, fontSize: '0.85rem',
                  border: 0, borderRadius: 10, cursor: 'pointer', boxShadow: '0 6px 18px rgba(255,107,53,0.5)',
                }}>
                  <FileText size={15} /> Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes tax-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes tax-slide { from { opacity: 0; transform: translateY(20px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes tax-pulse { 0%, 100% { transform: scale(1); box-shadow: 0 10px 28px rgba(220,38,38,0.45); } 50% { transform: scale(1.05); box-shadow: 0 14px 36px rgba(220,38,38,0.65); } }
      `}</style>
    </>
  );
};

export default TaxUrgencyPopup;
